import React, { useMemo, useState } from 'react';
import DashboardTable from './DashboardTable';
import IconButton from './IconButton';
import CircularProgress from './CircularProgress';

const OpenInvoices = ({ data = { current: 0, '30_plus_days': 0, '60_plus_days': 0, list: [] }, isLoading = false, onActionClick }) => {
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'open_due', 'paid'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Handle filter tab click
  const handleFilterClick = (filter, e) => {
    e.preventDefault();
    setActiveFilter(filter);
  };

  // Handle action clicks from table
  const handleActionClick = (row, action) => {
    if (action === 'print') {
      // Print functionality - currently no action
      // This is just a placeholder for future implementation
      return;
    }
    if (onActionClick) {
      onActionClick(row, action);
    }
  };
  // Extract data from API response structure
  const invoiceData = useMemo(() => {
    // Handle both old array format and new object format for backward compatibility
    if (Array.isArray(data)) {
      return {
        current: 0,
        '30_plus_days': 0,
        '60_plus_days': 0,
        list: data
      };
    }
    return {
      current: data?.current || 0,
      '30_plus_days': data?.['30_plus_days'] || 0,
      '60_plus_days': data?.['60_plus_days'] || 0,
      list: data?.list || []
    };
  }, [data]);

  // Calculate total for percentage calculation
  const totalAmount = useMemo(() => {
    return invoiceData.current + invoiceData['30_plus_days'] + invoiceData['60_plus_days'];
  }, [invoiceData]);

  // Calculate percentages for each period
  const currentPercentage = useMemo(() => {
    return totalAmount > 0 ? Math.round((invoiceData.current / totalAmount) * 100) : 0;
  }, [invoiceData.current, totalAmount]);

  const last30DaysPercentage = useMemo(() => {
    return totalAmount > 0 ? Math.round((invoiceData['30_plus_days'] / totalAmount) * 100) : 0;
  }, [invoiceData['30_plus_days'], totalAmount]);

  const last60DaysPercentage = useMemo(() => {
    return totalAmount > 0 ? Math.round((invoiceData['60_plus_days'] / totalAmount) * 100) : 0;
  }, [invoiceData['60_plus_days'], totalAmount]);

  // Format currency
  const formatCurrency = (amount) => {
    // return `$${parseFloat(amount || 0).toFixed(2)}`;
    return '-';
  };

  // Filter invoice list based on active filter, search, and sort
  const filteredTableData = useMemo(() => {
    let filtered = [...(invoiceData.list || [])];
    
    // Apply status filter
    if (activeFilter === 'open_due') {
      filtered = filtered.filter(invoice => {
        const status = (invoice.status || '').toLowerCase();
        return status !== 'paid';
      });
    } else if (activeFilter === 'paid') {
      filtered = filtered.filter(invoice => {
        const status = (invoice.status || '').toLowerCase();
        return status === 'paid';
      });
    }
    
    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(invoice => {
        const invoiceNumber = (invoice.invoice_number || '').toLowerCase();
        const invoiceDate = (invoice.invoice_date || '').toLowerCase();
        const dueDate = (invoice.due_date || '').toLowerCase();
        const status = (invoice.status || '').toLowerCase();
        return invoiceNumber.includes(term) || invoiceDate.includes(term) || dueDate.includes(term) || status.includes(term);
      });
    }
    
    // Apply sorting
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal = a[sortColumn] || '';
        let bVal = b[sortColumn] || '';
        
        // Handle date columns
        if (sortColumn === 'invoice_date' || sortColumn === 'due_date') {
          try {
            const aParts = aVal.split('/');
            const bParts = bVal.split('/');
            if (aParts.length === 3 && bParts.length === 3) {
              aVal = new Date(aParts[2], aParts[0] - 1, aParts[1]);
              bVal = new Date(bParts[2], bParts[0] - 1, bParts[1]);
            } else {
              aVal = new Date(0);
              bVal = new Date(0);
            }
          } catch (e) {
            aVal = new Date(0);
            bVal = new Date(0);
          }
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : (aVal < bVal ? -1 : 0);
        } else {
          return aVal < bVal ? 1 : (aVal > bVal ? -1 : 0);
        }
      });
    }
    // Temporarily hide all invoice table values by replacing them with '-'
    const masked = filtered.map((invoice) => ({
      ...invoice,
      invoice_number: '-',
      invoice_date: '-',
      due_date: '-',
      status: '-',
    }));

    // Limit to 3 rows in the table
    return masked.slice(0, 3);
  }, [invoiceData.list, activeFilter, searchTerm, sortColumn, sortDirection]);

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="col-lg-6 mb-2 pb-1">
      <div className="card mb-0 border-0 shadow-none">
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <h6 className="text-md font-weight-bold pr-2 mb-0">Open Invoices</h6>
              <ul className="nav sh_subnav border-0">
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeFilter === 'all' ? 'active' : ''}`}
                    onClick={(e) => handleFilterClick('all', e)}
                    aria-current={activeFilter === 'all' ? 'page' : undefined}
                  >
                    All
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeFilter === 'open_due' ? 'active' : ''}`}
                    onClick={(e) => handleFilterClick('open_due', e)}
                  >
                    Open / Due
                  </a>
                </li>
                <li>
                  <a 
                    href="#" 
                    className={`flex-sm-fill text-sm-center text-dark nav-link ${activeFilter === 'paid' ? 'active' : ''}`}
                    onClick={(e) => handleFilterClick('paid', e)}
                  >
                    Paid
                  </a>
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-1">
              <div className="px-1">
                <IconButton>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M9.76383 13.2721L13.4012 3.45876C13.5992 2.92409 13.0785 2.40409 12.5445 2.60209L2.72783 6.24209C2.11383 6.47009 2.16183 7.35409 2.79717 7.51343L7.35317 8.65809L8.49117 13.2021C8.65117 13.8381 9.53583 13.8868 9.76383 13.2721V13.2721Z" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M13.2449 2.75342L7.35156 8.66008" stroke="#1D2433" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </IconButton>
              </div>
              <div className="px-1">
                <IconButton>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.0013 4V5" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12.2426 3.75736C14.5858 6.10051 14.5858 9.89949 12.2426 12.2426C9.89948 14.5858 6.10049 14.5858 3.75736 12.2426C1.41421 9.89948 1.41421 6.10049 3.75736 3.75736C6.10051 1.41421 9.89949 1.41421 12.2426 3.75736" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8.0013 12V11" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 9.50333V9.50333C6 10.332 6.67133 11.0033 7.5 11.0033H8.59533C9.37067 11.0033 10 10.3747 10 9.59867V9.59867C10 8.95467 9.562 8.39333 8.93733 8.23667L7.06267 7.76667C6.438 7.61 6 7.04867 6 6.40467V6.40467C6 5.62867 6.62867 5 7.40467 5H8.5C9.32867 5 10 5.67133 10 6.5V6.5" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </IconButton>
              </div>
              <div>
                <button type="button" className="btn sh_btn btn-primary">Pay Now</button>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body px-0 pt-0 mt-2 pb-2" style={{ position: 'relative', overflow: 'hidden', maxHeight: '220px' }}>
          <div className="row" style={{ margin: 0, display: 'flex', alignItems: 'flex-start', height: '100%' }}>
            <div className="col-4 bg-white" style={{ 
              position: 'sticky', 
              left: 0, 
              top: 0, 
              zIndex: 10,
              alignSelf: 'flex-start',
              paddingRight: '15px',
              height: 'fit-content'
            }}>
              <div className="d-flex sh_statusIndicatorbox pale-purple px-3 py-1 mb-2">
                <CircularProgress percentage={currentPercentage} color="#7D4BC5" />
                <div className="d-flex flex-column">
                  <p className="p1-light text-dark mb-0">Current</p>
                  <h3 className="h3-strong mb-0">{formatCurrency(invoiceData.current)}</h3>
                </div>
              </div>
              <div className="d-flex sh_statusIndicatorbox olivegreen px-3 py-1 mb-2">
                <CircularProgress percentage={last30DaysPercentage} color="#0EA472" />
                <div className="d-flex flex-column">
                  <p className="p1-light text-dark mb-0">Last 30 Days</p>
                  <h3 className="h3-strong mb-0">{formatCurrency(invoiceData['30_plus_days'])}</h3>
                </div>
              </div>
              <div className="d-flex sh_statusIndicatorbox pale-purple px-3 py-1 mb-2">
                <CircularProgress percentage={last60DaysPercentage} color="#7D4BC5" />
                <div className="d-flex flex-column">
                  <p className="p1-light text-dark mb-0">Last 60 Days</p>
                  <h3 className="h3-strong mb-0">{formatCurrency(invoiceData['60_plus_days'])}</h3>
                </div>
              </div>
            </div>
            <div className="col-8" style={{ 
              maxHeight: '220px',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'thin',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="px-2 pt-2 pb-1 d-flex align-items-center gap-2" style={{ borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
                    <div className="flex-grow-1">
                      <div className="input-group" style={{ maxWidth: '300px' }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search invoices..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                          style={{ fontSize: '0.875rem' }}
                        />
                        <div className="input-group-append">
                          <span className="input-group-text" style={{ padding: '0.25rem 0.5rem' }}>
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M14 14L11.1 11.1" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                    {searchTerm && (
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => setSearchTerm('')}
                        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <DashboardTable
                      columns={['INVOICE NUMBER', 'INVOICE DATE', 'DUE DATE', 'STATUS', 'ACTION']}
                      data={filteredTableData}
                      onActionClick={handleActionClick}
                      sortable={true}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      getCellContent={(col) => {
                        if (col === 'ACTION') {
                          return '-';
                        }
                        return null;
                      }}
                      // actionIcons currently disabled; will implement later
                      // actionIcons={[
                      //   {
                      //     svg: (
                      //       <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      //         <path d="M15 5.00008V2.50008C14.9998 2.03991 14.6268 1.66691 14.1667 1.66675H5.83333C5.37316 1.66691 5.00017 2.03991 5 2.50008V5.00008" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //         <path d="M14.168 7.49992H15.0013" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //         <path d="M5 14.1667H4.16667C3.24619 14.1667 2.5 13.4205 2.5 12.5V6.66667C2.5 5.74619 3.24619 5 4.16667 5H15.8333C16.7538 5 17.5 5.74619 17.5 6.66667V12.5C17.5 13.4205 16.7538 14.1667 15.8333 14.1667H15" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //         <path d="M12.5 12.4999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //         <path d="M12.5 14.9999H7.5" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //         <rect x="5" y="10" width="10" height="7.5" rx="1" stroke="#323232" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      //       </svg>
                      //     ),
                      //     title: 'Print Invoice',
                      //     action: 'print'
                      //   }
                      // ]}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenInvoices;


